import React, { useEffect } from "react";
import { useForm } from "react-hook-form";

import classes from "./termsAndConditionsDetail.module.css";
import { FormInput } from "../shared/formElements/formInput/FormInput";
import { FormRichText } from "../shared/formElements/formRichText/FormRichtext";
import { CascadingDropdowns } from "../shared/formElements/cascadingDropdown/CascadingDropdown";

const CATEGORY_ITEMS = [
  {
    id: 1,
    menu_id: 1,
    name: "Electronics",
    parent_id: null,
    level: 1,
    is_bulk: 0,
  },
  {
    id: 2,
    menu_id: 2,
    name: "Smartphones",
    parent_id: 1,
    level: 2,
    is_bulk: 0,
  },
  {
    id: 3,
    menu_id: 6,
    name: "Spares",
    parent_id: 2,
    level: 3,
    is_bulk: 0,
  },
  {
    id: 4,
    menu_id: 9,
    name: "Spares",
    parent_id: 2,
    level: 3,
    is_bulk: 1,
  },
];

export const TermsAndConditionsDetail = ({
  initialValues,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onChange",
  });

  // Register custom fields once on mount — validators are preserved
  useEffect(() => {
    register("content", {
      required: "Content is required",
      validate: (val) => {
        const text = new DOMParser()
          .parseFromString(val || "", "text/html")
          .body.textContent.trim();
        return text.length > 0 || "Content is required";
      },
    });

    register("category_id", {
      required: "Category is required",
    });
  }, [register]);

  // Sync values when data loads — keepIsValid preserves validation state
  useEffect(() => {
    if (!initialValues) return;

    // keepValues: false → full reset; keepErrors: false → revalidate
    reset(initialValues, { keepDefaultValues: false });

    // Override category_id with the leaf node after reset
    if (initialValues.sub_category_id) {
      setValue("category_id", initialValues.sub_category_id, {
        shouldValidate: true,
      });
    }

    // Re-sync content since reset will have set it correctly already,
    // but trigger validation so the editor error clears
    if (initialValues.content) {
      setValue("content", initialValues.content, { shouldValidate: true });
    }
  }, [initialValues, reset, setValue]);

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Terms & Conditions</h3>
        <div className={classes.grid}>
          <FormInput
            id="title"
            label="Title"
            register={register}
            validation={{ required: "Title is required" }}
            error={errors.title?.message}
            required
          />
        </div>
      </div>

      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Category</h3>
        <CascadingDropdowns
          items={CATEGORY_ITEMS}
          name="category_id"
          setValue={setValue}
          error={errors.category_id?.message}
          initialIds={
            initialValues
              ? {
                  main_category_id: initialValues.main_category_id,
                  category_id: initialValues.category_id,
                  sub_category_id: initialValues.sub_category_id,
                }
              : null
          }
        />
      </div>

      <div className={classes.section}>
        <FormRichText
          id="content"
          label="Content"
          value={watch("content")}
          onChange={(val) => setValue("content", val, { shouldValidate: true })}
          required
          error={errors.content?.message}
        />
      </div>

      <div className={classes.actions}>
        <button
          type="button"
          className={classes.deleteBtn}
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
        <button
          type="submit"
          className={classes.submitBtn}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
      </div>
    </form>
  );
};
